import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrendingUpIcon, DollarSignIcon, AlertTriangleIcon, LightbulbIcon, CalendarIcon, PlayIcon, BookOpenIcon } from 'lucide-react';
export function ProgramsOverviewPage() {
  const navigate = useNavigate();
  // Seed data for programs
  const programs = [{
    id: 1,
    name: 'In-Market Account Acceleration',
    type: 'New Logo',
    playbooks: 2,
    plays: 5,
    targetAccounts: 500,
    pacing: 82,
    forecastContribution: 2800000,
    status: 'on-pace'
  }, {
    id: 2,
    name: 'Event-Driven Pipeline',
    type: 'Acquire',
    playbooks: 3,
    plays: 7,
    targetAccounts: 300,
    pacing: 95,
    forecastContribution: 3200000,
    status: 'on-pace'
  }, {
    id: 3,
    name: 'Opportunity Re-Engagement',
    type: 'Close',
    playbooks: 2,
    plays: 4,
    targetAccounts: 250,
    pacing: 65,
    forecastContribution: 1800000,
    status: 'off-pace'
  }, {
    id: 4,
    name: 'Product-Led Expansion',
    type: 'Expand',
    playbooks: 2,
    plays: 6,
    targetAccounts: 200,
    pacing: 88,
    forecastContribution: 2400000,
    status: 'on-pace'
  }, {
    id: 5,
    name: 'Champion Activation',
    type: 'Expand',
    playbooks: 1,
    plays: 3,
    targetAccounts: 150,
    pacing: 72,
    forecastContribution: 1600000,
    status: 'off-pace'
  }];
  const totalPrograms = programs.length;
  const pipelineFromPrograms = programs.reduce((sum, p) => sum + p.forecastContribution, 0);
  const offPacePrograms = programs.filter(p => p.status === 'off-pace').length;
  const recommendedAdds = 3;
  const recommendations = [{
    id: 1,
    title: 'Intent-Based Engagement',
    reason: 'Acquire stage is under-invested',
    expectedLift: '+25-30%',
    playbooks: 1,
    plays: 8
  }, {
    id: 2,
    title: 'Conversion Optimization',
    reason: 'Close rate is below benchmark',
    expectedLift: '+15-20%',
    playbooks: 1,
    plays: 6
  }, {
    id: 3,
    title: 'Account Warming',
    reason: 'Cold outreach conversion is low',
    expectedLift: '+18-22%',
    playbooks: 1,
    plays: 5
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-pace':
        return 'bg-green-100 text-green-700';
      case 'off-pace':
        return 'bg-yellow-100 text-yellow-700';
      case 'paused':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'New Logo':
      case 'Acquire':
        return 'bg-blue-100 text-blue-700';
      case 'Close':
        return 'bg-purple-100 text-purple-700';
      case 'Expand':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-6275">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-6276">
        <div className="flex items-center justify-between" data-id="element-6277">
          <div data-id="element-6278">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6279">
              Programs Overview
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-6280">
              See how your current GTM programs are contributing to pipeline and
              expansion. Add or adjust programs to stay on pace.
            </p>
          </div>
          <button onClick={() => navigate('/programs/new')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-6281">
            <PlusIcon className="w-4 h-4" data-id="element-6282" />
            New Program
          </button>
        </div>
      </div>
      <div className="p-6 space-y-6" data-id="element-6283">
        {/* Summary Tiles */}
        <div className="grid grid-cols-4 gap-6" data-id="element-6284">
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6285">
            <div className="flex items-center justify-between mb-2" data-id="element-6286">
              <p className="text-sm text-gray-600" data-id="element-6287">Total Programs Live</p>
              <PlayIcon className="w-5 h-5 text-blue-600" data-id="element-6288" />
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-6289">{totalPrograms}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6290">
            <div className="flex items-center justify-between mb-2" data-id="element-6291">
              <p className="text-sm text-gray-600" data-id="element-6292">Pipeline from Programs</p>
              <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-6293" />
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-6294">
              ${(pipelineFromPrograms / 1000000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-6295">This quarter</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6296">
            <div className="flex items-center justify-between mb-2" data-id="element-6297">
              <p className="text-sm text-gray-600" data-id="element-6298">Off-Pace Programs</p>
              <AlertTriangleIcon className="w-5 h-5 text-yellow-600" data-id="element-6299" />
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-6300">
              {offPacePrograms}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6301">
            <div className="flex items-center justify-between mb-2" data-id="element-6302">
              <p className="text-sm text-gray-600" data-id="element-6303">Recommended Adds</p>
              <LightbulbIcon className="w-5 h-5 text-purple-600" data-id="element-6304" />
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-6305">
              {recommendedAdds}
            </p>
          </div>
        </div>
        {/* Program Roster */}
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-6306">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-6307">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-6308">
              Program Roster
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-6309">
            <table className="w-full" data-id="element-6310">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-6311">
                <tr data-id="element-6312">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6313">
                    Program Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6314">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6315">
                    Playbooks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6316">
                    Plays
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6317">
                    Pacing
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6318">
                    Forecast
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6319">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-6320">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-6321">
                {programs.map(program => <tr key={program.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/programs/${program.id}`)} data-id="element-6322">
                    <td className="px-6 py-4" data-id="element-6323">
                      <p className="text-sm font-medium text-gray-900" data-id="element-6324">
                        {program.name}
                      </p>
                    </td>
                    <td className="px-6 py-4" data-id="element-6325">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(program.type)}`} data-id="element-6326">
                        {program.type}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6327">
                      <div className="flex items-center gap-1 text-sm text-gray-900" data-id="element-6328">
                        <BookOpenIcon className="w-4 h-4 text-purple-600" data-id="element-6329" />
                        {program.playbooks}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6330">
                      <div className="flex items-center gap-1 text-sm text-gray-900" data-id="element-6331">
                        <PlayIcon className="w-4 h-4 text-blue-600" data-id="element-6332" />
                        {program.plays}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6333">
                      <div className="flex items-center gap-2" data-id="element-6334">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6335">
                          <div className={`h-full rounded-full ${program.pacing >= 80 ? 'bg-green-600' : 'bg-yellow-600'}`} style={{
                        width: `${program.pacing}%`
                      }} data-id="element-6336" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-6337">
                          {program.pacing}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6338">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-6339">
                        ${(program.forecastContribution / 1000000).toFixed(1)}M
                      </p>
                    </td>
                    <td className="px-6 py-4" data-id="element-6340">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(program.status)}`} data-id="element-6341">
                        {program.status}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6342">
                      <button className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-6343">
                        Open
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        {/* Gaps and Recommendations */}
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-6344">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-6345">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-6346">
              Gaps & Recommendations
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-6347">
              Based on your current program mix and performance
            </p>
          </div>
          <div className="p-6" data-id="element-6348">
            <div className="grid grid-cols-3 gap-6" data-id="element-6349">
              {recommendations.map(rec => <div key={rec.id} className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-6350">
                  <div className="flex items-start gap-3 mb-3" data-id="element-6351">
                    <LightbulbIcon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-6352" />
                    <div className="flex-1" data-id="element-6353">
                      <h3 className="text-sm font-semibold text-purple-900 mb-1" data-id="element-6354">
                        {rec.title}
                      </h3>
                      <p className="text-xs text-purple-700 mb-2" data-id="element-6355">
                        {rec.reason}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-purple-700 mb-3" data-id="element-6356">
                        <span data-id="element-6357">Expected lift: {rec.expectedLift}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-purple-600" data-id="element-6358">
                        <span data-id="element-6359">{rec.playbooks} playbook</span>
                        <span data-id="element-6360">•</span>
                        <span data-id="element-6361">{rec.plays} plays</span>
                      </div>
                    </div>
                  </div>
                  <button className="w-full px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors" data-id="element-6362">
                    Add to Programs
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        {/* Program Timeline */}
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-6363">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-6364">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-6365">
              Program Timeline
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-6366">
              Week-by-week view of program activity
            </p>
          </div>
          <div className="p-6" data-id="element-6367">
            <div className="h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-6368">
              <div className="text-center" data-id="element-6369">
                <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" data-id="element-6370" />
                <p className="text-gray-500" data-id="element-6371">
                  Timeline visualization would appear here
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}